import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";

const DOCUMENT_FOCUS_POLL_MS = 1000;

const GlobalTabActivityContext = createContext<boolean | null>(null);
const GlobalDocumentFocusContext = createContext<boolean | null>(null);

function useTabActiveLocal(enabled: boolean): boolean {
  const [isActive, setIsActive] = useState(() =>
    typeof document === "undefined" ? true : document.visibilityState === "visible",
  );

  const onChange = useCallback(() => {
    setIsActive(document.visibilityState === "visible");
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    onChange();
    document.addEventListener("visibilitychange", onChange);

    return () => {
      document.removeEventListener("visibilitychange", onChange);
    };
  }, [enabled, onChange]);

  return isActive;
}

export function useTabActive(): boolean {
  const sharedIsActive = useContext(GlobalTabActivityContext);
  const localIsActive = useTabActiveLocal(sharedIsActive == null);

  return sharedIsActive ?? localIsActive;
}

function useDocumentHasFocusLocal(shouldPollFocus: boolean): boolean {
  const [hasFocus, setHasFocus] = useState(() =>
    typeof document === "undefined" ? true : document.hasFocus(),
  );

  useEffect(() => {
    if (!shouldPollFocus) {
      return;
    }

    setHasFocus(document.hasFocus());

    const interval = setInterval(() => {
      setHasFocus(document.hasFocus());
    }, DOCUMENT_FOCUS_POLL_MS);

    return () => clearInterval(interval);
  }, [shouldPollFocus]);

  return hasFocus;
}

export function useDocumentHasFocus(shouldPollFocus: boolean) {
  const sharedHasFocus = useContext(GlobalDocumentFocusContext);
  const localHasFocus = useDocumentHasFocusLocal(shouldPollFocus && sharedHasFocus == null);

  return sharedHasFocus ?? localHasFocus;
}

export function useTabActiveAndFocused(
  onFocus?: () => void,
  debounceSeconds: number = 60,
): boolean {
  const tabIsActive = useTabActive();
  const documentHasFocus = useDocumentHasFocus(tabIsActive);
  const focusedAndActive = tabIsActive && documentHasFocus;
  const timeUnfocusedMs = useRef<number | null>(null);
  const wasFocusedAndActive = useRef(focusedAndActive);

  useEffect(() => {
    if (focusedAndActive) {
      if (!wasFocusedAndActive.current) {
        const unfocusedAtMs = timeUnfocusedMs.current;
        timeUnfocusedMs.current = null;

        if (onFocus != null && unfocusedAtMs != null) {
          const secondsUnfocused = (Date.now() - unfocusedAtMs) / 1000;

          if (secondsUnfocused > debounceSeconds) {
            onFocus();
          }
        }
      }
    } else if (wasFocusedAndActive.current) {
      timeUnfocusedMs.current = Date.now();
    }

    wasFocusedAndActive.current = focusedAndActive;
  }, [focusedAndActive, onFocus, debounceSeconds]);

  return focusedAndActive;
}

function useSharedTabFocusState() {
  const tabIsActive = useTabActiveLocal(true);
  const documentHasFocus = useDocumentHasFocusLocal(tabIsActive);
  const contextValue = useMemo(
    () => ({
      tabIsActive,
      documentHasFocus,
    }),
    [tabIsActive, documentHasFocus],
  );

  return contextValue;
}

export const GLOBAL_IDLE_TIMEOUT = 60_000; // 1 minute

export const GlobalIdleContext = createContext<boolean>(false);

export const GlobalIdleWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const [idle, setIdle] = useState(false);
  const { tabIsActive, documentHasFocus } = useSharedTabFocusState();
  const focusedAndActive = tabIsActive && documentHasFocus;

  useIdleTimer({
    timeout: GLOBAL_IDLE_TIMEOUT,
    onIdle: () => setIdle(true),
    onActive: () => setIdle(false),
    eventsThrottle: 500,
  });

  const finalIdleState = idle || !focusedAndActive;

  return (
    <GlobalTabActivityContext.Provider value={tabIsActive}>
      <GlobalDocumentFocusContext.Provider value={documentHasFocus}>
        <GlobalIdleContext.Provider value={finalIdleState}>{children}</GlobalIdleContext.Provider>
      </GlobalDocumentFocusContext.Provider>
    </GlobalTabActivityContext.Provider>
  );
};
